import { Button, Flex, Typography, Upload } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import FormItem from '@/components/form/FormItem';

import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import type { RcFile } from 'antd/lib/upload';
import type { ReactNode } from 'react';

type PersistedUploadFile = {
  id: string;
  name: string;
  url: string;
};

type MultiFileUploadProps = {
  label?: string;
  help?: ReactNode;
  accept?: string;
  maxCount?: number;
  /** Files already stored on the server. */
  persistedFiles?: PersistedUploadFile[];
  /** Stored files queued for deletion — hidden until the caller saves. */
  pendingRemovalIds?: string[];
  /** Files picked but not uploaded yet, owned by the caller. */
  stagedFiles?: UploadFile[];
  onStagedFilesChange?: (files: UploadFile[]) => void;
  onRemovePersisted?: (id: string) => void;
  beforeUpload?: (file: RcFile) => boolean;
  chooseFileLabel?: string;
  dataTestId?: string;
};

/** The caller-owned draft: everything the list renders and reports back. */
export type MultiFileUploadDraftProps = Required<
  Pick<
    MultiFileUploadProps,
    | 'persistedFiles'
    | 'pendingRemovalIds'
    | 'stagedFiles'
    | 'onStagedFilesChange'
    | 'onRemovePersisted'
    | 'beforeUpload'
  >
>;

/**
 * Renders stored and staged files as one list. Holds no state: the caller owns
 * both collections so the list can never drift from what a save will send.
 */
export function MultiFileUpload({
  label,
  help,
  accept,
  maxCount = 10,
  persistedFiles = [],
  pendingRemovalIds = [],
  stagedFiles = [],
  onStagedFilesChange,
  onRemovePersisted,
  beforeUpload,
  chooseFileLabel,
  dataTestId,
}: MultiFileUploadProps) {
  const { t } = useTranslation();

  const persistedIds = useMemo(
    () => new Set(persistedFiles.map((file) => file.id)),
    [persistedFiles],
  );

  const fileList = useMemo(() => {
    const pendingRemoval = new Set(pendingRemovalIds);
    const persistedList: UploadFile[] = persistedFiles
      .filter((file) => !pendingRemoval.has(file.id))
      .map((file) => ({
        uid: file.id,
        name: file.name,
        url: file.url,
        status: 'done' as const,
      }));
    return [...persistedList, ...stagedFiles];
  }, [persistedFiles, pendingRemovalIds, stagedFiles]);

  const canChooseMore = fileList.length < maxCount;

  const handleBeforeUpload = (uploaded: RcFile) => {
    if (beforeUpload && !beforeUpload(uploaded)) {
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const handleChange: UploadProps['onChange'] = (info) => {
    onStagedFilesChange?.(
      info.fileList.filter((file) => !persistedIds.has(file.uid)),
    );
  };

  const handleRemove: UploadProps['onRemove'] = (file) => {
    if (!persistedIds.has(file.uid)) {
      return true;
    }
    onRemovePersisted?.(file.uid);
    return false;
  };

  return (
    <Flex vertical gap="small" data-testid={dataTestId}>
      <FormItem label={label} help={help}>
        <Upload
          name="files"
          accept={accept}
          fileList={fileList}
          multiple
          maxCount={maxCount}
          beforeUpload={handleBeforeUpload}
          customRequest={() => void 0}
          onChange={handleChange}
          onRemove={handleRemove}
          isImageUrl={() => false}
          showUploadList={{
            showPreviewIcon: false,
            showRemoveIcon: true,
            showDownloadIcon: false,
          }}
        >
          {canChooseMore ? (
            <Button type="default">
              {chooseFileLabel ?? t('CHOOSE_FILE')}
            </Button>
          ) : null}
        </Upload>
      </FormItem>
      {!canChooseMore ? (
        <Typography.Text type="secondary">
          {t('MAX_FILES_REACHED')}
        </Typography.Text>
      ) : null}
    </Flex>
  );
}
